import type { ApiTarget } from "./account-api.js";
import { RequestDraftConflict } from "./note-store.js";
import { attachmentRequestInput, deliverRequest, draftStore, selectedAttachments, type PendingRequest } from "./attachment-outbox.js";
import type { RaiseLedgerRequest } from "./platform-client.js";

/** One durable request per account; retries reuse its frozen payload and identities. */
export function requestComposer(dialog: HTMLElement, origin: ApiTarget | (() => ApiTarget), accepted: (id: string, quarantined: boolean) => void) {
  const prompt = dialog.querySelector<HTMLTextAreaElement>("[data-ask-prompt]")!;
  const picker = dialog.querySelector<HTMLInputElement>("[data-ask-file]")!;
  const status = dialog.querySelector<HTMLElement>("[data-ask-copied]")!;
  const submit = dialog.querySelector<HTMLButtonElement>("[data-action='submit-ask']")!;
  const discard = dialog.querySelector<HTMLButtonElement>("[data-ask-discard]")!;
  let account = "";
  let draft: PendingRequest | undefined;
  let busy = false;
  let epoch = 0;
  let writes = Promise.resolve();
  let version: { revision: string | undefined } = { revision: undefined };
  function render() {
    prompt.disabled = busy || draft?.submitted === true;
    picker.disabled = busy || draft?.submitted === true;
    submit.disabled = busy || !draft;
    discard.disabled = busy || !draft;
    submit.textContent = busy ? "Working…" : draft?.submitted ? "Retry saved request" : "Submit for later";
    dialog.querySelector<HTMLElement>("[data-ask-attachment]")!.textContent = draft?.attachments[0]
      ? `${draft.attachments[0].name} · ${(draft.attachments[0].bytes.byteLength / 1024 / 1024).toFixed(1)} MiB`
      : "Optional: one file, up to 40 MiB.";
  }
  function save() {
    const currentAccount = account;
    const currentDraft = draft && structuredClone(draft);
    const currentVersion = version;
    writes = writes.catch(() => {}).then(async () => {
      if (!currentDraft) return;
      currentDraft.revision = currentVersion.revision;
      await draftStore(currentAccount, "write", currentDraft);
      currentVersion.revision = currentDraft.revision;
    });
    return writes;
  }
  prompt.addEventListener("input", () => {
    if (!draft || draft.submitted) return;
    draft.input = { ...draft.input, body: prompt.value };
    void save().catch((error: Error) => { status.textContent = `Could not save this request: ${error.message}`; });
  });
  discard.addEventListener("click", () => { void (async () => {
    if (!draft || busy) return;
    const current = epoch;
    busy = true; render();
    try {
      draft = { id: "pending", input: { ...draft.input, body: prompt.value, idempotencyKey: crypto.randomUUID() }, attachments: [], submitted: false };
      await save();
      status.textContent = "Started a new request without attachments. Any earlier submitted request or uploaded file remains in the vault.";
    } catch (error) { status.textContent = error instanceof Error ? error.message : "Could not start a new request."; }
    finally { if (current === epoch) { busy = false; render(); } }
  })(); });
  picker.addEventListener("change", () => { void (async () => {
    if (!draft || busy || draft.submitted) return;
    const current = epoch;
    busy = true; render();
    try {
      const attachments = await selectedAttachments(Array.from(picker.files ?? []));
      if (current !== epoch) return;
      draft.attachments = attachments;
      await save();
      status.textContent = attachments.length ? "Attachment saved. You can return to this request after reloading." : "Attachment removed.";
    } catch (error) { if (current === epoch) status.textContent = error instanceof Error ? error.message : "Could not save the attachment."; }
    finally { if (current === epoch) { busy = false; picker.value = ""; render(); } }
  })(); });
  return {
    async open(nextAccount: string, input: RaiseLedgerRequest) {
      const current = ++epoch;
      account = nextAccount; busy = true; draft = undefined; render();
      try {
        await writes.catch(() => {});
        if (current !== epoch) return;
        const saved = await draftStore(account, "read");
        if (current !== epoch) return;
        draft = saved ?? { id: "pending", input, attachments: [], submitted: false };
        version = { revision: saved?.revision };
        prompt.value = draft.input.body;
        status.textContent = saved ? "Restored your saved request. Retry continues the same submission." : "";
        if (saved === undefined) await save();
      } catch (error) { if (current === epoch) status.textContent = `Cannot save a durable request: ${error instanceof Error ? error.message : "storage unavailable"}`; }
      finally { if (current === epoch) { busy = false; render(); } }
    },
    async submit() {
      if (!draft || busy || !prompt.value.trim()) return;
      const current = epoch;
      busy = true;
      if (!draft.submitted) draft.input = { ...draft.input, body: prompt.value.trim() };
      try { attachmentRequestInput(draft); }
      catch (error) { status.textContent = error instanceof Error ? error.message : "Shorten the request."; busy = false; render(); return; }
      draft.submitted = true;
      render();
      let persisted = false;
      try {
        await save();
        persisted = true;
        const id = await deliverRequest(typeof origin === "function" ? origin() : origin, draft, (text) => { if (current === epoch) status.textContent = text; }, save, () => current === epoch);
        if (current !== epoch) return;
        const quarantined = draft.quarantined === true;
        try { await draftStore(account, "remove", { ...draft, revision: version.revision }); }
        catch (error) { if (!(error instanceof RequestDraftConflict)) throw error; }
        // A different tab may already have started a new pending request. The
        // accepted old request is acknowledged without deleting that newer work.
        draft = undefined;
        accepted(id, quarantined);
      } catch (error) { if (current === epoch) status.textContent = error instanceof RequestDraftConflict ? error.message
        : `${error instanceof Error ? error.message : "Request failed."} ${persisted ? "Your request remains saved here." : "Could not save this request. Keep this page open and retry."}`; }
      finally { if (current === epoch) { busy = false; render(); } }
    },
    copyText() {
      if (draft?.attachments.length && !draft.frozenInput) throw new Error("Submit the saved attachment first. Copying now would leave its file out of the request.");
      return draft?.frozenInput?.body ?? prompt.value;
    },
    async reset() { epoch++; account = ""; draft = undefined; busy = false; prompt.value = ""; picker.value = ""; render(); await writes.catch(() => {}); },
  };
}
